import { MarketplaceSettings } from 'types/Marketplace';

const HEX_COLOR_REGEX = /^#[0-9A-F]{6}$/i;

const hexToRgb = (hex: string): { r: number; g: number; b: number } => ({
    r: parseInt(hex.substring(1, 3), 16),
    g: parseInt(hex.substring(3, 5), 16),
    b: parseInt(hex.substring(5, 7), 16)
});

// Shifts HSL lightness down, mirroring the relation between the default vars (--primary/--primary-dark).
const darken = (hex: string, amount: number): string => {
    const { r, g, b } = hexToRgb(hex);
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
        if (max === rNorm) h = ((gNorm - bNorm) / delta) % 6;
        else if (max === gNorm) h = (bNorm - rNorm) / delta + 2;
        else h = (rNorm - gNorm) / delta + 4;
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    const l = (max + min) / 2;
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    const darkenedL = Math.max(0, l - amount / 100);

    const c = (1 - Math.abs(2 * darkenedL - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = darkenedL - c / 2;

    let rgb: [number, number, number];
    if (h < 60) rgb = [c, x, 0];
    else if (h < 120) rgb = [x, c, 0];
    else if (h < 180) rgb = [0, c, x];
    else if (h < 240) rgb = [0, x, c];
    else if (h < 300) rgb = [x, 0, c];
    else rgb = [c, 0, x];

    const toHex = (value: number) =>
        Math.round((value + m) * 255)
            .toString(16)
            .padStart(2, '0');

    return `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`;
};

const LIGHT_FOREGROUND = '#ffffff';
const DARK_FOREGROUND = '#222222';

// https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
const getRelativeLuminance = (hex: string): number => {
    const { r, g, b } = hexToRgb(hex);

    const [rLin, gLin, bLin] = [r, g, b].map(channel => {
        const norm = channel / 255;

        return norm <= 0.03928 ? norm / 12.92 : Math.pow((norm + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
};

// Past the max-contrast crossover on purpose: the design keeps white even at low
// contrast (the default #eab464 sits at 1.87:1), so only near-white backgrounds flip.
const WHITE_TEXT_LUMINANCE_LIMIT = 0.6;

const getReadableForeground = (background: string): string =>
    getRelativeLuminance(background) > WHITE_TEXT_LUMINANCE_LIMIT
        ? DARK_FOREGROUND
        : LIGHT_FOREGROUND;

export const applyBrandColors = (settings: MarketplaceSettings): void => {
    const root = document.documentElement.style;
    const { brandPrimaryColor, brandSecondaryColor } = settings;

    if (brandPrimaryColor && HEX_COLOR_REGEX.test(brandPrimaryColor)) {
        const { r, g, b } = hexToRgb(brandPrimaryColor);
        root.setProperty('--primary', brandPrimaryColor);
        root.setProperty('--primary-dark', darken(brandPrimaryColor, 17));
        root.setProperty('--primary-disabled', `rgba(${r}, ${g}, ${b}, 0.6)`);
        root.setProperty('--primary-foreground', getReadableForeground(brandPrimaryColor));
    }

    if (brandSecondaryColor && HEX_COLOR_REGEX.test(brandSecondaryColor)) {
        root.setProperty('--green', brandSecondaryColor);
        root.setProperty('--green-dark', darken(brandSecondaryColor, 19));
        root.setProperty('--green-foreground', getReadableForeground(brandSecondaryColor));
    }
};
